import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import { downloadFile, FileType, fileTypes } from 'commons/utils/file'
import {
  Alert,
  BatchSearch,
  Breadcrumb,
  FileUploadToast,
  Icon,
  INITIAL_PAGE,
  Multiselect,
  Page,
  type PaginationParams,
  SlimButton,
} from 'components'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  downloadClustersMaterials,
  filterClustersMaterials,
  getTechnologies,
  type LeadTimesData,
  type RequestClustersMaterialsData,
  type Technology,
  type UploadExcelResponseData,
  uploadClustersFile,
} from 'services/leadTimes/'

import { LeadTimesTable, TopBar, TrackingModal, UploadSpreadsheetTableError } from './components'
import * as S from './styles'

const LeadTimes = () => {
  const [loading, setLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [openTracking, setOpenTracking] = useState(false)
  const [technologies, setTechnologies] = useState<Technology[]>([])
  const [leadTimesData, setLeadTimesData] = useState<LeadTimesData[]>([])
  const [pagination, setPagination] = useState<PaginationParams>({
    currentPage: INITIAL_PAGE,
    totalItems: 0,
  })
  const [uploadErrorsData, setUploadErrorsData] = useState<UploadExcelResponseData>({
    issues: [],
    markedExcelUrl: null,
  })
  const uploadToastId = useRef<string | number | null>(null)

  const defaultFields: {
    technologies: string[]
    materials: string
  } = {
    materials: '',
    technologies: [],
  }

  const methods = useForm({
    defaultValues: defaultFields,
  })

  const { handleSubmit, getValues, reset } = methods

  const fetchData = async (requestData: RequestClustersMaterialsData, page: number) => {
    // NOTE: Get only technology Id
    const technologyIds = requestData.technologies.map((tech) => tech.split(' - ')[0])

    try {
      setLoading(true)
      const { data, pagination: paginationResponse } = await filterClustersMaterials(
        { ...requestData, technologies: technologyIds },
        {
          page,
        },
      )

      setLeadTimesData(data)
      setPagination(paginationResponse)
    } catch (error) {
      console.error('Error on fetch data: ', error)
      setLeadTimesData([])
      setPagination({ currentPage: INITIAL_PAGE, totalItems: 0 })
    } finally {
      setLoading(false)
    }
  }

  const fetchInitialData = async () => {
    setLoading(true)

    try {
      const [clustersMaterials, techs] = await Promise.all([
        filterClustersMaterials({ materials: [], technologies: [] }, { page: INITIAL_PAGE }),
        getTechnologies(),
      ])

      const { data, pagination: paginationResponse } = clustersMaterials

      setLeadTimesData(data)
      setPagination(paginationResponse)
      setTechnologies(techs)
    } catch (error) {
      console.error('Error on fetch initial data: ', error)
      setLeadTimesData([])
      setPagination({ currentPage: INITIAL_PAGE, totalItems: 0 })
      setTechnologies([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    const { materials, technologies } = getValues()
    const requestBody = {
      materials: materials ? materials.split(',').filter(Boolean) : [],
      technologies: technologies,
    }

    await fetchData(requestBody, INITIAL_PAGE)
  }

  const handlePageChange = async (page: number) => {
    setPagination({
      ...pagination,
      currentPage: page,
    })
    const { materials, technologies } = getValues()
    const requestBody = {
      materials: materials ? materials.split(',').filter(Boolean) : [],
      technologies: technologies,
    }

    await fetchData(requestBody, page)
  }

  const handleClear = async () => {
    reset(defaultFields)

    await fetchData({ materials: [], technologies: [] }, INITIAL_PAGE)
  }

  const handleBack = () => {
    setUploadErrorsData({
      issues: [],
      markedExcelUrl: null,
    })
  }

  const setUploadProgress = (progress: number) => {
    if (uploadToastId.current) {
      toast.update(uploadToastId.current, {
        render: <FileUploadToast progress={progress} />,
      })
    } else {
      uploadToastId.current = toast(<FileUploadToast progress={progress} />, {
        autoClose: false,
        closeOnClick: false,
      })
    }
  }

  const dismissUploadToast = () => {
    if (uploadToastId.current) {
      toast.dismiss(uploadToastId.current)
      uploadToastId.current = null
    }
  }

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true)

    if (event.target.files?.length) {
      const uploadedFile = event.target.files[0]

      if (uploadedFile.type !== fileTypes.Excel) {
        toast.error('Só é possível enviar planilhas no formato ".xlsx".')
      } else {
        const form = new FormData()
        form.append('file', uploadedFile)

        uploadClustersFile(form, setUploadProgress)
          .then((response) => {
            if (response.status === 200) {
              dismissUploadToast()
              toast.success('Alterações salvas com sucesso! Veja os detalhes acessando o Tracking.')
              //NOTE: clear filters and reload data on success
              handleClear()
            }
          })
          .catch((error) => {
            dismissUploadToast()
            toast.error('Erro ao processar o arquivo.')
            setUploadErrorsData(error.response.data.error.details)
          })
          .finally(() => {
            //NOTE: Clear input value to allow re-uploading the same file
            event.target.value = ''
            setIsUploading(false)
          })
      }
    }
  }

  const handleDownload = async () => {
    toast.info('O download começará em instantes.')

    try {
      const file = await downloadClustersMaterials()

      downloadFile({
        file,
        fileName: 'planilha-clusters.xlsx',
        fileType: FileType.Excel,
      })
    } catch (error) {
      toast.error('Houve um problema ao iniciar o download. Tente novamente mais tarde')
      console.error('Error on download file: ', error)
    }
  }

  const handleErrorDownload = () => {
    if (uploadErrorsData.markedExcelUrl) {
      window.open(uploadErrorsData.markedExcelUrl, '_blank')
    }
  }

  const renderAlert = () => {
    return (
      <Alert
        fullWidth
        kind={'error'}
        title={
          'Erros na planilha! Verifique os detalhes abaixo e faça as correções necessárias antes de reenviá-la.'
        }
      />
    )
  }

  const renderTopBar = () => {
    return (
      <TopBar
        disabled={!!uploadErrorsData.issues.length || loading || isUploading}
        onDownloadClick={handleDownload}
        onFileSelect={handleUpload}
        onTrackingClick={() => setOpenTracking(true)}
      />
    )
  }

  return (
    <Page fullwidth loading={loading} renderHeaderComponent={renderTopBar()}>
      <Breadcrumb />
      <S.Wrapper>
        <S.Header>
          <S.TitleContainer>
            <S.Title>Lead Times</S.Title>
          </S.TitleContainer>
          <S.Divider />
          {!uploadErrorsData.issues.length && (
            <FormProvider {...methods}>
              <S.Form>
                <S.FieldsContainer>
                  <S.FieldWrapper mediaTemplateColumns="1fr 1fr" templateColumns="1fr 1fr 1fr">
                    <S.FieldItemWrapper>
                      <S.LabelFilter>Material</S.LabelFilter>
                      <Controller
                        name="materials"
                        render={({ field }) => (
                          <BatchSearch
                            height={30}
                            onChange={(val) => field.onChange(val)}
                            placeHolder="Buscar por códigos (Ex: 4000029)"
                            value={field.value || ''}
                          />
                        )}
                      />
                    </S.FieldItemWrapper>
                    <S.FieldItemWrapper>
                      <S.LabelFilter>Tecnologia</S.LabelFilter>
                      <Controller
                        name="technologies"
                        render={({ field }) => {
                          return (
                            <Multiselect
                              inputPlaceholder="Clique para selecionar"
                              onChange={(value) => field.onChange(value)}
                              optionsList={technologies.map((tech) => `${tech.id} - ${tech.name}`)}
                              selectedOptions={field.value}
                              width={323}
                            />
                          )
                        }}
                      />
                    </S.FieldItemWrapper>
                    <S.FormActionsContainer>
                      <SlimButton
                        content="Buscar"
                        id="filters-submit-button"
                        onClick={handleSubmit(handleFilter)}
                        size="sm"
                        type="submit"
                        variant="primary"
                      />
                      <SlimButton
                        content="Limpar"
                        icon={<Icon icon="clear-all" size="20" />}
                        id="filters-clear-button"
                        onClick={handleClear}
                        size="sm"
                        type="button"
                        variant="ghost"
                      />
                    </S.FormActionsContainer>
                  </S.FieldWrapper>
                </S.FieldsContainer>
                <S.TextInfo>{pagination.totalItems} resultados totais</S.TextInfo>
              </S.Form>
            </FormProvider>
          )}

          {uploadErrorsData.issues.length ? (
            <>
              {renderAlert()}

              <S.ActionsContainer>
                <SlimButton
                  content="Voltar"
                  icon={<Icon icon="chevron-left" size="20" />} // TODO: Replace with arrow-left icon
                  onClick={handleBack}
                  type="submit"
                  variant="ghost"
                />
                <SlimButton
                  content="Baixar"
                  icon={<Icon icon="download" size="20" />}
                  id="filters-clear-button"
                  onClick={handleErrorDownload}
                  type="button"
                  variant="primary"
                />
              </S.ActionsContainer>

              <UploadSpreadsheetTableError data={uploadErrorsData.issues} />
            </>
          ) : (
            <LeadTimesTable
              data={leadTimesData}
              pagination={pagination}
              setCurrentPage={handlePageChange}
            />
          )}
        </S.Header>
      </S.Wrapper>
      {openTracking && <TrackingModal setOpen={setOpenTracking} />}
    </Page>
  )
}

export default LeadTimes
